import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Alert,
} from "@mui/material";
import { useUserContext } from "../../context/UserContext";
import { completeGoogleProfile } from "../../client/api";
import LogoContainer from "../../components/LogoContainer";
import Logo from "../../components/Logo";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGlobalStyles } from "../../theme/global";

const CompleteProfile: React.FC = () => {
  const [organizationKey, setOrganizationKey] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const { setUser } = useUserContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const classes = useGlobalStyles();

  // Get userId from URL parameters (passed from Google callback)
  useEffect(() => {
    const urlUserId = searchParams.get("userId");
    if (urlUserId) {
      setUserId(urlUserId);
    } else {
      setError("User identification missing. Please sign in again.");
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!organizationKey) {
      setError("Organization key is required");
      setLoading(false);
      return;
    }

    if (!userId) {
      setError("User identification missing. Please sign in again.");
      setLoading(false);
      return;
    }

    try {
      const result = await completeGoogleProfile(organizationKey, searchParams.get("token") || "");

      // Update user context
      setUser(result.user);

      // Store the token for future requests
      localStorage.setItem("token", result.token);
      if (result.refreshToken) {
        localStorage.setItem("refreshToken", result.refreshToken);
      }

      navigate(
        `/auth/success?userId=${userId}&token=${encodeURIComponent(result.token)}&refreshToken=${encodeURIComponent(result.refreshToken || "")}`
      );
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || "Failed to complete profile"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 400, margin: "auto", padding: 2 }}>
      <LogoContainer>
        <Logo />
        <Typography variant="h6" sx={{ color: "#000", fontWeight: "bold" }}>
          LetsLaunch
        </Typography>
      </LogoContainer>
      <Typography variant="h5">Complete Your Profile</Typography>
      <br />

      <Typography variant="body1" sx={{ mb: 3 }}>
        Welcome! Please provide your organization key to continue.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Organization Key"
          value={organizationKey}
          onChange={(e) => setOrganizationKey(e.target.value)}
          required
          sx={{ mb: 3 }}
          className={classes.textFieldLabel}
        />

        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={loading || !userId}
        >
          {loading ? "Completing..." : "Complete Sign Up"}
        </Button>
      </Box>
    </Box>
  );
};

export default CompleteProfile;
